/**
 * AI Assistant Widget - Floating Chat for Dental Clinic Knowledge
 * Provides AI-powered answers about dental procedures, medications, and care
 * With security features: permission checking, input sanitization, action confirmation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useViewOnly } from '../contexts';
import AIConfirmationModal from './modals/AIConfirmationModal';
import './AIAssistantWidget.css';

/**
 * Sanitize user input to prevent injection attacks
 */
const sanitizeInput = (input) => {
  if (!input) return '';
  return input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove JS protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .slice(0, 1000); // Limit length
};

const AIAssistantWidget = ({ language = 'he' }) => {
  const { authFetch, user } = useAuth();
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [hasPermission, setHasPermission] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  // Stable per-widget-mount session id — backend v2 keeps conversation
  // memory keyed on this, so follow-up questions have context.
  const [sessionId] = useState(() =>
    (window.crypto?.randomUUID?.() || `widget-${Date.now()}-${Math.floor(Math.random() * 1e6)}`)
  );

  // External opener: the Sentinel Bear (or anything else) dispatches this
  // event to open the chat — replaces the floating 💬 trigger button.
  useEffect(() => {
    const onExternalOpen = () => {
      setIsOpen((prev) => !prev);
      setIsMinimized(false);
    };
    window.addEventListener('taisacore:toggle-assistant', onExternalOpen);
    return () => window.removeEventListener('taisacore:toggle-assistant', onExternalOpen);
  }, []);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Translations
  const t = {
    he: {
      title: 'עוזר AI',
      subtitle: 'שאל אותי על טיפולי שיניים',
      placeholder: 'הקלד שאלה...',
      send: 'שלח',
      typing: 'מקליד...',
      suggestions: 'שאלות נפוצות',
      welcome: 'שלום! אני העוזר הדיגיטלי של מרפאת יהלומי. אני יכול לענות על שאלות על טיפולי שיניים, תרופות, והכנה לטיפולים. במה אוכל לעזור?',
      error: 'מצטער, לא הצלחתי לעבד את השאלה. נסה שוב.',
      copied: 'הועתק!',
      copy: 'העתק'
    },
    en: {
      title: 'AI Assistant',
      subtitle: 'Ask me about dental care',
      placeholder: 'Type a question...',
      send: 'Send',
      typing: 'Typing...',
      suggestions: 'Common Questions',
      welcome: "Hello! I'm the digital assistant for Yahalomi Dental Clinic. I can answer questions about dental procedures, medications, and treatment preparation. How can I help?",
      error: 'Sorry, I could not process your question. Please try again.',
      copied: 'Copied!',
      copy: 'Copy',
      noPermission: 'You do not have permission to use the AI assistant.',
      confirmRequired: 'This action requires confirmation.'
    }
  }[language];

  // Check AI permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await authFetch('/api/security/ai-permissions');
        if (response.ok) {
          const data = await response.json();
          setHasPermission(data.can_use_assistant);
          if (!data.can_use_assistant) {
            setPermissionError(t.noPermission);
          }
        }
      } catch (error) {
        console.error('Failed to check AI permissions:', error);
        // Allow access if permission check fails (graceful degradation)
        setHasPermission(true);
      }
    };

    if (user) {
      checkPermissions();
    }
  }, [authFetch, user, t.noPermission]);

  // Fetch suggestions on mount
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await authFetch(`/api/assistant/suggestions?language=${language}`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      }
    };

    fetchSuggestions();
  }, [authFetch, language]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 1,
        role: 'assistant',
        content: t.welcome,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length, t.welcome]);

  // Scroll to bottom when new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Handle sending message
  const handleSend = useCallback(async () => {
    if (!input.trim() || isTyping || !hasPermission) return;

    // Sanitize input
    const sanitizedInput = sanitizeInput(input.trim());
    if (!sanitizedInput) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: sanitizedInput,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await authFetch('/api/assistant/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: sanitizedInput,
          language: language,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Check if action requires confirmation
        if (data.requires_confirmation && data.confirmation) {
          setPendingConfirmation(data.confirmation);
          setMessages(prev => [...prev, {
            id: Date.now(),
            role: 'assistant',
            content: t.confirmRequired,
            timestamp: new Date().toISOString(),
            isConfirmation: true
          }]);
        } else {
          setMessages(prev => [...prev, {
            id: Date.now(),
            role: 'assistant',
            content: data.answer,
            timestamp: data.timestamp
          }]);
        }
      } else if (response.status === 403) {
        // Permission denied
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: t.noPermission,
          timestamp: new Date().toISOString(),
          isError: true
        }]);
        setHasPermission(false);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: t.error,
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [input, isTyping, hasPermission, authFetch, language, sessionId, t.error, t.noPermission, t.confirmRequired]);

  // Handle confirmation approval
  const handleConfirmApprove = useCallback(async (confirmationId) => {
    setConfirmationLoading(true);
    try {
      const response = await authFetch(`/api/security/confirm/${confirmationId}`, {
        method: 'POST',
        body: JSON.stringify({ approved: true })
      });

      if (response.ok) {
        await response.json();
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: language === 'he' ? 'הפעולה אושרה ובוצעה בהצלחה.' : 'Action approved and executed successfully.',
          timestamp: new Date().toISOString()
        }]);
        setPendingConfirmation(null);
      }
    } catch (error) {
      console.error('Confirmation failed:', error);
    } finally {
      setConfirmationLoading(false);
    }
  }, [authFetch, language]);

  // Handle confirmation rejection
  const handleConfirmReject = useCallback(async (confirmationId, reason) => {
    setConfirmationLoading(true);
    try {
      const response = await authFetch(`/api/security/confirm/${confirmationId}`, {
        method: 'POST',
        body: JSON.stringify({ approved: false, reason })
      });

      if (response.ok) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: language === 'he' ? 'הפעולה בוטלה.' : 'Action cancelled.',
          timestamp: new Date().toISOString()
        }]);
        setPendingConfirmation(null);
      }
    } catch (error) {
      console.error('Rejection failed:', error);
    } finally {
      setConfirmationLoading(false);
    }
  }, [authFetch, language]);

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  // Copy message to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Minimize widget
  const minimizeWidget = () => {
    setIsMinimized(true);
  };

  // Close widget
  const closeWidget = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  return (
    <div className={`ai-assistant-widget ${language === 'he' ? 'rtl' : 'ltr'}`}>
      {/* Floating trigger removed — the Sentinel Bear opens the chat
          (dispatches 'taisacore:toggle-assistant'; see App.js). */}

      {/* Chat Panel */}
      {isOpen && (
        <div className={`ai-chat-panel ${isMinimized ? 'minimized' : ''}`}>
          {/* Header */}
          <div className="chat-header">
            <div className="header-info">
              <span className="header-icon">🦷</span>
              <div>
                <h3>{t.title}</h3>
                <p>{t.subtitle}</p>
              </div>
            </div>
            <div className="header-actions">
              <button className="action-btn minimize" onClick={minimizeWidget}>
                _
              </button>
              <button className="action-btn close" onClick={closeWidget}>
                ×
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="chat-messages">
                {messages.map(msg => (
                  <div key={msg.id} className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                    <div className="message-content">
                      {msg.content}
                    </div>
                    {msg.role === 'assistant' && !msg.isError && (
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(msg.content)}
                        title={t.copy}
                      >
                        📋
                      </button>
                    )}
                  </div>
                ))}
                {isTyping && (
                  <div className="message assistant typing">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggestions */}
              {suggestions.length > 0 && messages.length <= 1 && (
                <div className="suggestions-container">
                  <p className="suggestions-label">{t.suggestions}</p>
                  <div className="suggestions-list">
                    {suggestions.slice(0, 4).map((suggestion, index) => (
                      <button
                        key={index}
                        className={`suggestion-chip ${isViewOnly ? 'disabled-view-only' : ''}`}
                        onClick={() => handleSuggestionClick(suggestion.question)}
                        disabled={isViewOnly}
                        title={isViewOnly ? viewOnlyTip : undefined}
                      >
                        {suggestion.question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="chat-input">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={hasPermission ? t.placeholder : t.noPermission}
                  disabled={isTyping || !hasPermission}
                />
                <button
                  className={`send-btn ${isViewOnly ? 'disabled-view-only' : ''}`}
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping || !hasPermission || isViewOnly}
                  title={isViewOnly ? viewOnlyTip : undefined}
                >
                  {language === 'he' ? '➤' : '➤'}
                </button>
              </div>

              {/* Permission Error */}
              {!hasPermission && permissionError && (
                <div className="permission-error">
                  🔒 {permissionError}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* AI Confirmation Modal */}
      {pendingConfirmation && (
        <AIConfirmationModal
          confirmation={pendingConfirmation}
          language={language}
          onApprove={handleConfirmApprove}
          onReject={handleConfirmReject}
          onClose={() => setPendingConfirmation(null)}
          isLoading={confirmationLoading}
        />
      )}
    </div>
  );
};

export default AIAssistantWidget;
